import { useEffect } from 'react';
import './SkyUppercutAnimation.css';

/**
 * PROFESSIONAL SKY UPPERCUT ANIMATION
 * Fighting-type physical move with powerful upward punch mechanics
 * 
 * Key Features:
 * - Fighting-type physical attack (85 base power, 90% accuracy)
 * - Can hit Pokémon using Fly, Bounce, or Sky Drop
 * - Powerful upward punch with sky-reaching effects
 * - Professional gaming company quality with martial arts timing
 * - Physical move animation with uppercut mechanics and aerial impact
 */
export default function SkyUppercutAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    // Get battle container for positioning
    const container = playerEl.closest('.battle-screen') || playerEl.closest('.battle-container') || document.body;
    const containerRect = container.getBoundingClientRect();
    
    // Calculate positions
    const playerRect = playerEl.getBoundingClientRect();
    const opponentRect = opponentEl.getBoundingClientRect();
    
    const playerX = Math.round((playerRect.left + playerRect.width / 2) - containerRect.left);
    const playerY = Math.round((playerRect.top + playerRect.height / 2) - containerRect.top);
    const opponentX = Math.round((opponentRect.left + opponentRect.width / 2) - containerRect.left);
    const opponentY = Math.round((opponentRect.top + opponentRect.height / 2) - containerRect.top);

    // Calculate uppercut trajectory
    const dx = opponentX - playerX;
    const dy = opponentY - playerY;
    const distance = Math.hypot(dx, dy);

    // Elements to clean up
    const elements = [];

    // PHASE 1: Uppercut Stance Preparation (0-400ms)
    console.log('Sky Uppercut Phase 1: Uppercut Stance Preparation');
    
    // Player uppercut stance with fighting energy
    playerEl.classList.add('sky-uppercut-user-stance');
    
    // Fighting energy aura around player
    const fightingAura = document.createElement('div');
    fightingAura.className = 'sky-uppercut-fighting-aura';
    fightingAura.style.left = `${playerX - 85}px`;
    fightingAura.style.top = `${playerY - 85}px`;
    fightingAura.style.width = '170px';
    fightingAura.style.height = '170px';
    container.appendChild(fightingAura);
    elements.push(fightingAura);

    // Uppercut energy particles around player
    for (let i = 0; i < 14; i++) {
      const energyParticle = document.createElement('div');
      energyParticle.className = 'sky-uppercut-energy-particle';
      const angle = (i / 14) * 360;
      const radius = 55 + (i % 3) * 12;
      const particleX = playerX + Math.cos(angle * Math.PI / 180) * radius;
      const particleY = playerY + Math.sin(angle * Math.PI / 180) * radius;
      energyParticle.style.left = `${particleX - 5}px`;
      energyParticle.style.top = `${particleY - 5}px`;
      energyParticle.style.animationDelay = `${i * 25}ms`;
      energyParticle.textContent = '👊';
      container.appendChild(energyParticle);
      elements.push(energyParticle);
    }

    // Power concentration indicators
    for (let i = 0; i < 5; i++) {
      const powerIndicator = document.createElement('div');
      powerIndicator.className = 'sky-uppercut-power-indicator';
      powerIndicator.style.left = `${playerX + (i % 2 === 0 ? -25 : 25)}px`;
      powerIndicator.style.top = `${playerY + (i * 6) - 20}px`;
      powerIndicator.style.animationDelay = `${i * 60}ms`;
      powerIndicator.textContent = '⚡';
      container.appendChild(powerIndicator);
      elements.push(powerIndicator);
    }

    // Uppercut charging effect
    const uppercutCharge = document.createElement('div');
    uppercutCharge.className = 'sky-uppercut-charge-effect';
    uppercutCharge.style.left = `${playerX - 20}px`;
    uppercutCharge.style.top = `${playerY + 10}px`;
    uppercutCharge.style.width = '40px';
    uppercutCharge.style.height = '20px';
    container.appendChild(uppercutCharge);
    elements.push(uppercutCharge);

    // PHASE 2: Explosive Uppercut Launch (400-800ms)
    const phase2Timer = setTimeout(() => {
      console.log('Sky Uppercut Phase 2: Explosive Uppercut Launch');
      
      // Player uppercut launch movement
      playerEl.classList.remove('sky-uppercut-user-stance');
      playerEl.classList.add('sky-uppercut-user-launch');
      playerEl.style.setProperty('--launch-x', `${dx * 0.7}px`);
      playerEl.style.setProperty('--launch-y', `${dy * 0.7}px`);

      // Create uppercut energy path
      for (let i = 0; i < 6; i++) {
        const energyPath = document.createElement('div');
        energyPath.className = 'sky-uppercut-energy-path';
        const pathProgress = (i + 1) / 6;
        const pathX = playerX + (dx * 0.7 * pathProgress);
        const pathY = playerY + (dy * 0.7 * pathProgress) - (i * 8); // Add upward curve
        energyPath.style.left = `${pathX - 12}px`;
        energyPath.style.top = `${pathY - 12}px`;
        energyPath.style.width = '24px';
        energyPath.style.height = '24px';
        energyPath.style.animationDelay = `${i * 50}ms`;
        container.appendChild(energyPath);
        elements.push(energyPath);
      }

      // Uppercut force indicators
      for (let i = 0; i < 8; i++) {
        const forceTimer = setTimeout(() => {
          const forceIndicator = document.createElement('div');
          forceIndicator.className = 'sky-uppercut-force-indicator';
          const progress = i / 8;
          const forceX = playerX + (dx * 0.7 * progress) + (Math.random() - 0.5) * 20;
          const forceY = playerY + (dy * 0.7 * progress) + (Math.random() - 0.5) * 15;
          forceIndicator.style.left = `${forceX - 4}px`;
          forceIndicator.style.top = `${forceY - 4}px`;
          forceIndicator.textContent = '💨';
          container.appendChild(forceIndicator);
          elements.push(forceIndicator);
        }, i * 35);
        elements.push({ timer: forceTimer, type: 'timer' });
      }

      // Sky-reaching energy burst
      for (let i = 0; i < 6; i++) {
        const skyBurstTimer = setTimeout(() => {
          const skyBurst = document.createElement('div');
          skyBurst.className = 'sky-uppercut-sky-burst';
          const burstX = playerX + (dx * 0.7 * (i / 6)) + (Math.random() - 0.5) * 30;
          const burstY = playerY + (dy * 0.7 * (i / 6)) - (i * 15);
          skyBurst.style.left = `${burstX - 6}px`;
          skyBurst.style.top = `${burstY - 6}px`;
          skyBurst.style.animationDelay = `${i * 40}ms`;
          skyBurst.textContent = '⬆️';
          container.appendChild(skyBurst);
          elements.push(skyBurst);
        }, i * 45);
        elements.push({ timer: skyBurstTimer, type: 'timer' });
      }
    }, 400);
    elements.push({ timer: phase2Timer, type: 'timer' });

    // PHASE 3: Sky Impact and Aerial Hit (800-1200ms)
    const phase3Timer = setTimeout(() => {
      console.log('Sky Uppercut Phase 3: Sky Impact and Aerial Hit');
      
      // Opponent aerial hit reaction
      opponentEl.classList.add('sky-uppercut-opponent-hit');

      // Reset player position with impact effect
      playerEl.classList.remove('sky-uppercut-user-launch');
      playerEl.classList.add('sky-uppercut-user-impact');

      // Massive sky impact explosion
      const skyImpact = document.createElement('div');
      skyImpact.className = 'sky-uppercut-sky-impact';
      skyImpact.style.left = `${opponentX - 110}px`;
      skyImpact.style.top = `${opponentY - 150}px`;
      skyImpact.style.width = '220px';
      skyImpact.style.height = '220px';
      container.appendChild(skyImpact);
      elements.push(skyImpact);

      // Aerial impact burst particles
      for (let i = 0; i < 18; i++) {
        const impactBurst = document.createElement('div');
        impactBurst.className = 'sky-uppercut-impact-burst';
        const burstAngle = (i / 18) * 360;
        const burstRadius = 70 + Math.random() * 45;
        const burstX = opponentX + Math.cos(burstAngle * Math.PI / 180) * burstRadius;
        const burstY = opponentY + Math.sin(burstAngle * Math.PI / 180) * burstRadius - 30;
        impactBurst.style.left = `${burstX - 5}px`;
        impactBurst.style.top = `${burstY - 5}px`;
        impactBurst.style.animationDelay = `${i * 20}ms`;
        impactBurst.style.setProperty('--burst-x', `${Math.cos(burstAngle * Math.PI / 180) * 35}px`);
        impactBurst.style.setProperty('--burst-y', `${Math.sin(burstAngle * Math.PI / 180) * 35 - 20}px`);
        impactBurst.textContent = '💥';
        container.appendChild(impactBurst);
        elements.push(impactBurst);
      }

      // Sky shockwaves
      for (let i = 0; i < 3; i++) {
        const shockwaveTimer = setTimeout(() => {
          const shockwave = document.createElement('div');
          shockwave.className = 'sky-uppercut-shockwave';
          shockwave.style.left = `${opponentX - 90}px`;
          shockwave.style.top = `${opponentY - 90}px`;
          shockwave.style.width = '180px';
          shockwave.style.height = '180px';
          shockwave.style.animationDelay = `${i * 100}ms`;
          container.appendChild(shockwave);
          elements.push(shockwave);
        }, i * 120);
        elements.push({ timer: shockwaveTimer, type: 'timer' });
      }

      // High-altitude hit indicators
      for (let i = 0; i < 8; i++) {
        const altitudeHit = document.createElement('div');
        altitudeHit.className = 'sky-uppercut-altitude-hit';
        const hitX = opponentX + (Math.random() - 0.5) * 100;
        const hitY = opponentY + (Math.random() - 0.5) * 80 - 40;
        altitudeHit.style.left = `${hitX - 4}px`;
        altitudeHit.style.top = `${hitY - 4}px`;
        altitudeHit.style.animationDelay = `${i * 35}ms`;
        altitudeHit.textContent = '🌟';
        container.appendChild(altitudeHit);
        elements.push(altitudeHit);
      }
    }, 800);
    elements.push({ timer: phase3Timer, type: 'timer' });

    // PHASE 4: Aerial Descent and Landing (1200-1600ms)
    const phase4Timer = setTimeout(() => {
      console.log('Sky Uppercut Phase 4: Aerial Descent and Landing');
      
      // Remove impact effects
      playerEl.classList.remove('sky-uppercut-user-impact');
      playerEl.classList.add('sky-uppercut-user-recovery');
      
      // Aerial descent effect
      const aerialDescent = document.createElement('div');
      aerialDescent.className = 'sky-uppercut-aerial-descent';
      aerialDescent.style.left = `${opponentX - 60}px`;
      aerialDescent.style.top = `${opponentY - 100}px`;
      aerialDescent.style.width = '120px';
      aerialDescent.style.height = '120px';
      container.appendChild(aerialDescent);
      elements.push(aerialDescent);

      // Descent particles
      for (let i = 0; i < 10; i++) {
        const descentParticle = document.createElement('div');
        descentParticle.className = 'sky-uppercut-descent-particle';
        const descentX = opponentX + (Math.random() - 0.5) * 80;
        const descentY = opponentY + (Math.random() - 0.5) * 60 - 50;
        descentParticle.style.left = `${descentX - 4}px`;
        descentParticle.style.top = `${descentY - 4}px`;
        descentParticle.style.animationDelay = `${i * 30}ms`;
        descentParticle.textContent = '⬇️';
        container.appendChild(descentParticle);
        elements.push(descentParticle);
      }

      // Landing impact effect
      const landingImpact = document.createElement('div');
      landingImpact.className = 'sky-uppercut-landing-impact';
      landingImpact.style.left = `${opponentX + 25}px`;
      landingImpact.style.top = `${opponentY - 30}px`;
      landingImpact.textContent = 'THUD!';
      container.appendChild(landingImpact);
      elements.push(landingImpact);
    }, 1200);
    elements.push({ timer: phase4Timer, type: 'timer' });

    // CLEANUP (1600ms)
    const cleanupTimer = setTimeout(() => {
      console.log('Sky Uppercut: Animation Complete - Cleanup');
      
      // Remove all CSS classes
      playerEl.classList.remove('sky-uppercut-user-stance', 'sky-uppercut-user-launch', 'sky-uppercut-user-impact', 'sky-uppercut-user-recovery');
      opponentEl.classList.remove('sky-uppercut-opponent-hit');
      
      // Remove all created elements
      elements.forEach(item => {
        if (item.type === 'timer') {
          clearTimeout(item.timer);
        } else if (item && item.parentNode) {
          item.remove();
        }
      });
      
      // Animation complete
      onComplete();
    }, 1600);

    // Cleanup function for component unmount
    return () => {
      clearTimeout(cleanupTimer);
      playerEl.classList.remove('sky-uppercut-user-stance', 'sky-uppercut-user-launch', 'sky-uppercut-user-impact', 'sky-uppercut-user-recovery');
      opponentEl.classList.remove('sky-uppercut-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') {
          clearTimeout(item.timer);
        } else if (item && item.parentNode) {
          item.remove();
        }
      });
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

// Set move type for battle system
SkyUppercutAnimation.moveType = 'fighting';
